/**
 * AuditLogLink - Small icon button that links to Global Audit filtered by resource type
 *
 * Placed next to settings page titles to provide quick access to audit history
 * for that specific resource type.
 */

import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface AuditLogLinkProps {
  /** Resource types to filter by (e.g., ['company'] or ['rate_profile', 'rate_assignment']) */
  resourceTypes: string[];
  /** Tooltip text */
  tooltip?: string;
  /** Additional CSS classes */
  className?: string;
}

export function AuditLogLink({
  resourceTypes,
  tooltip = 'View audit history',
  className,
}: AuditLogLinkProps) {
  const params = new URLSearchParams();
  params.set('resource_types', resourceTypes.join(','));

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-7 w-7 text-muted-foreground hover:text-[#8FB560] transition-colors',
              className
            )}
            asChild
          >
            <Link to={`/settings/audit-logs?${params.toString()}`}>
              <FileText className="h-4 w-4" />
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
